import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { BiMenuAltRight } from "react-icons/bi";
import { IoIosArrowForward } from "react-icons/io";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import { useBookFilterContext } from "./BookFilterContext";
import { formData } from "./booksQueryParams";
import useInitialQueryData from "admin/Filters/useInitialQueryData";

const BookFilter = ({
	genres,
	categories,
	publishers,
	numberOfActiveFilters = 0,
}) => {
	const hasRoles = useAuthStore((state) => state.hasRoles);
	const [visible, setVisible] = useState(false);
	const { filters } = useBookFilterContext();

	const { filterValues, setFilterValues } = useInitialQueryData(
		filters,
		formData
	);

	const changeInputValue = (e) => {
		const value = e.target?.value;
		const updatedFilters = { ...filterValues, [e.target.name]: value };
		setFilterValues(updatedFilters);
		// filters?.changeAllFiltersAtOnce(updatedFilters);
		// setVisible(false);
	};

	const handleApplyFilters = (e) => {
		e.preventDefault();
		filters?.changeAllFiltersAtOnce(filterValues);
		setVisible(false);
	};

	const handleClearFilters = () => {
		const params = filters?.allSearchParams
			?.filter((param) => !filters?.paginationParams?.includes(param))
			?.map((param) => ({
				[param]: "",
			}))
			.reduce((acc, curr) => ({ ...acc, ...curr }), {});

		setFilterValues(params);
		filters?.changeAllFiltersAtOnce({
			...params,
		});
		setVisible(false);
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setVisible(!visible)}
				className="flex items-center gap-1 h-11 border border-gray-300 rounded-lg min-w-max px-4"
			>
				Advanced filter
				<BiMenuAltRight size={20} />
			</button>

			<Sidebar
				visible={visible}
				onHide={() => setVisible(false)}
				position="right"
				className="w-full md:w-[350px] border-l-0 shadow-2xl"
				header={
					<div className="flex items-center gap-3 px-2">
						<div className="bg-akgreener p-2 rounded-lg text-white shadow-sm">
							<BiMenuAltRight size={20} />
						</div>
						<h3 className="font-bold text-lg text-gray-900">Advanced Filters</h3>
					</div>
				}
			>
				<form
					onSubmit={handleApplyFilters}
					className="flex flex-col gap-6 p-2"
				>
					<div className="space-y-4">
						<div className="space-y-3">
							<p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-1">Organization</p>
							
							{hasRoles([
								Role.SuperAdmin,
								Role.Admin,
								Role.Institution,
								Role.Educator,
							]) && (
								<div className="relative group">
									<select
										name="publisherIs"
										id="publisherIs"
										value={filterValues?.publisherIs}
										onChange={changeInputValue}
										className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
											filterValues?.publisherIs
												? "border-akgreen text-akgreener font-medium ring-2 ring-akgreen/10"
												: "border-gray-200 hover:border-gray-300 text-gray-600"
										}`}
									>
										<option value="">- All Publishers -</option>
										{publishers?.data?.map((publisher) => (
											<option key={publisher.id} value={publisher.id}>
												{publisher.name}
											</option>
										))}
									</select>
									<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
										<IoIosArrowForward className="rotate-90" size={14} />
									</div>
								</div>
							)}
						</div>

						<div className="space-y-3">
							<p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-1">Classification</p>
							
							<div className="relative">
								<select
									name="genreIs"
									id="genreIs"
									value={filterValues?.genreIs}
									onChange={changeInputValue}
									className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
										filterValues?.genreIs
											? "border-akgreen text-akgreener font-medium ring-2 ring-akgreen/10"
											: "border-gray-200 hover:border-gray-300 text-gray-600"
									}`}
								>
									<option value="">- All Collections -</option>
									{genres?.data?.data?.map((genre) => (
										<option key={genre.id} value={genre.name}>
											{genre.name}
										</option>
									))}
								</select>
								<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
									<IoIosArrowForward className="rotate-90" size={14} />
								</div>
							</div>

							<div className="relative">
								<select
									name="categoryIs"
									id="categoryIs"
									value={filterValues?.categoryIs}
									onChange={changeInputValue}
									className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
										filterValues?.categoryIs
											? "border-akgreen text-akgreener font-medium ring-2 ring-akgreen/10"
											: "border-gray-200 hover:border-gray-300 text-gray-600"
									}`}
								>
									<option value="">- All Categories -</option>
									{categories?.data?.map((category) => (
										<option key={category.id} value={category.name}>
											{category.name}
										</option>
									))}
								</select>
								<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
									<IoIosArrowForward className="rotate-90" size={14} />
								</div>
							</div>
						</div>

						{hasRoles([
							Role.SuperAdmin,
							Role.Admin,
							Role.SeniorPublisher,
							Role.Publisher,
						]) && (
							<div className="space-y-3">
								<p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-1">Status & Pricing</p>
								
								<div className="relative">
									<select
										name="pricingModelIs"
										id="pricingModelIs"
										className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
											filterValues?.pricingModelIs
												? "border-akgreen text-akgreener font-medium ring-2 ring-akgreen/10"
												: "border-gray-200 hover:border-gray-300 text-gray-600"
										}`}
										onChange={changeInputValue}
										value={filterValues.pricingModelIs}
									>
										<option value="">- Pricing Model -</option>
										<option value="sellable">Purchase only</option>
										<option value="subscription">Subscription only</option>
										<option value="all">Purchase & subscription</option>
										<option value="free">Free</option>
									</select>
									<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
										<IoIosArrowForward className="rotate-90" size={14} />
									</div>
								</div>

								<div className="relative">
									<select
										name="isActive"
										id="isActive"
										className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
											filterValues?.isActive
												? "border-akgreen text-akgreener font-medium ring-2 ring-akgreen/10"
												: "border-gray-200 hover:border-gray-300 text-gray-600"
										}`}
										onChange={changeInputValue}
										value={filterValues.isActive}
									>
										<option value="">- Activation Status -</option>
										<option value="1">Active</option>
										<option value="0">Inactive</option>
									</select>
									<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
										<IoIosArrowForward className="rotate-90" size={14} />
									</div>
								</div>

								<div className="space-y-3">
									<p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-1">File Availability</p>
									<div className="relative">
										<select
											name="withoutFileCollection"
											id="withoutFileCollection"
											className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
												filterValues?.withoutFileCollection
													? "border-red-500 text-red-700 font-medium ring-2 ring-red-50"
													: "border-gray-200 hover:border-gray-300 text-gray-600"
											}`}
											onChange={changeInputValue}
											value={filterValues.withoutFileCollection}
										>
											<option value="">- Missing Files -</option>
											<option value="book_cover">Missing book cover</option>
											<option value="book_file">Missing book file</option>
											<option value="all">Missing both files</option>
										</select>
										<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
											<IoIosArrowForward className="rotate-90" size={14} />
										</div>
									</div>

									<div className="relative">
										<select
											name="withFileCollection"
											id="withFileCollection"
											className={`w-full bg-white rounded-xl h-11 px-4 transition-all appearance-none cursor-pointer border ${
												filterValues?.withFileCollection
													? "border-akgreen text-akgreener font-medium ring-2 ring-akgreen/10"
													: "border-gray-200 hover:border-gray-300 text-gray-600"
											}`}
											onChange={changeInputValue}
											value={filterValues.withFileCollection}
										>
											<option value="">- Available Files -</option>
											<option value="book_cover">With book cover</option>
											<option value="book_file">With book file</option>
											<option value="all">With both files</option>
										</select>
										<div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
											<IoIosArrowForward className="rotate-90" size={14} />
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="mt-auto pt-6 flex flex-col gap-3">
						<button
							type="submit"
							className="w-full bg-akgreener hover:bg-akgreenest text-white font-bold h-12 rounded-xl transition-all shadow-md active:scale-95 flex items-center justify-center gap-2"
						>
							Apply Filters
						</button>

						{numberOfActiveFilters > 0 && (
							<button
								type="button"
								onClick={handleClearFilters}
								className="w-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 font-semibold h-12 rounded-xl transition-all flex items-center justify-center gap-2"
							>
								Clear All
								<span className="flex items-center rounded-full justify-center min-w-[20px] h-5 px-1.5 text-[10px] bg-gray-100 text-gray-500 font-bold border border-gray-200">
									{numberOfActiveFilters}
								</span>
							</button>
						)}
					</div>
				</form>
			</Sidebar>
		</>
	);
};

export default BookFilter;
